(() => {
  if (customElements.get('product-form')) {
    return;
  }

  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.submitButton = this.form?.querySelector('[name="add"]');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.productBar = document.querySelector(
        `#ProductBar-${this.dataset.sectionId}`
      );

      this.form.addEventListener(
        'submit',
        this.onSubmitHandler.bind(this)
      );
    }

    resetCustomStrapSelections() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;
      const selector = document.querySelector(`#ProductSelector-${sectionId}`);
      if (!selector) return;
      const checked = [
        ...selector.querySelectorAll('input[data-custom-strap-input]:checked')
      ];
      checked.forEach(input => {
        input.checked = false;
        input.closest('.product-options__item')?.classList.remove('is-active');
      });
    }

    resetCustomBottomsSelections() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;
      const selector = document.querySelector(`#ProductSelector-${sectionId}`);
      if (!selector) return;
      const checked = [
        ...selector.querySelectorAll('input[data-custom-bottoms-input]:checked')
      ];
      checked.forEach(input => {
        input.checked = false;
        input.closest('.product-options__item')?.classList.remove('is-active');
      });
    }

    refreshProductSelectorMedia() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;
      const selector = document.querySelector(`#ProductSelector-${sectionId}`);
      if (!selector) return;
      try {
        selector.updateOptions?.();
        selector.updateVariant?.();
        // Same fallback-to-variants[0] issue as resetProductSelectorToInitialState()
        // above -- this runs immediately after that reset, on the same
        // "nothing is selected" state, and would otherwise re-introduce
        // the wrong "current variant" that reset just cleared.
        if (!selector.hasAnyVariantOptionSelected?.()) {
          selector.currentVariant = null;
        }
        selector.syncAddButtonState?.();
        if (!selector.hasAnyVariantOptionSelected?.() && selector.form) {
          // syncAddButtonState()'s own "nothing selected" branch disables
          // the button but deliberately leaves its text alone (so it
          // doesn't say something wrong before the shopper has picked
          // anything, e.g. on a custom product's first load). Right here
          // specifically -- straight after a successful add-to-cart just
          // cleared the selection -- leaving the old text means the
          // button reads "Add to cart" while disabled, which looks like a
          // bug rather than a deliberate state. Label it explicitly.
          selector.form.toggleAddButton(true, window.theme?.strings?.soldOut || 'Sold out');
        }
        selector.updateMedia?.();
      } catch (e) {}
    }

    resetProductSelectorToInitialState() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;
      const selector = document.querySelector(`#ProductSelector-${sectionId}`);
      if (!selector) return;

      try {
        selector.isAdjusting = true;
        selector.clearInitialVariantOptionSelections?.();
        if (selector.mobileVariantStepperActive) {
          selector.mobileVariantStepperIndex = 0;
          selector.updateMobileVariantStepperUI?.(false);
        }
        selector.updateOptions?.();
        selector.updateVariant?.();
        // updateVariant() re-derives currentVariant via getVariantData(),
        // which -- when nothing is selected, as it never is right here --
        // falls back to variants[0] (whatever variant happens to be first
        // in the product's array order) rather than null. If that first
        // variant is sold out, syncAddButtonState() below reads it as the
        // "current" variant and disables Add to Cart showing "Sold out",
        // even though nothing is actually selected and the shopper hasn't
        // done anything wrong. Force it back to null here so
        // syncAddButtonState() hits its own already-correct
        // "nothing selected" branch instead.
        selector.currentVariant = null;
        selector.updateVariantTabsInteractivity?.();
        selector.syncAddButtonState?.();
        if (selector.form) {
          // Same reasoning as refreshProductSelectorMedia() below: this
          // fires straight after a successful add cleared the selection,
          // so a disabled button still reading "Add to cart" looks broken
          // rather than intentional. Label it explicitly.
          selector.form.toggleAddButton(true, window.theme?.strings?.soldOut || 'Sold out');
        }
      } catch (e) {}
      try {
        selector.isAdjusting = false;
      } catch (e) {}

      try {
        selector.prefilterMediaToFirstStyleAndColor?.();
      } catch (e) {}
    }

    /**
     * Primary add-to-cart + any duplicate submitters (sticky bar via [form], product bar).
     */
    getAddToCartButtons() {
      if (!this.form) {
        return [];
      }
      const set = new Set();
      this.form.querySelectorAll('button[name="add"]').forEach(b => set.add(b));
      const formId = this.form.getAttribute('id');
      if (formId) {
        document.querySelectorAll(`button[form="${formId}"]`).forEach(b => {
          if ((b.getAttribute('name') === 'add') && b.type === 'submit') {
            set.add(b);
          }
        });
      }
      const barBtn = this.productBar?.querySelector('[name="add"]');
      if (barBtn) {
        set.add(barBtn);
      }
      return Array.from(set);
    }

    setAddToCartLoading(isLoading) {
      this.getAddToCartButtons().forEach(btn => {
        if (isLoading) {
          btn.classList.add('disabled', 'is-loading');
          btn.setAttribute('aria-busy', 'true');
        } else {
          btn.classList.remove('disabled', 'is-loading');
          btn.removeAttribute('aria-busy');
        }
      });
    }

    getSelectedCustomStrapInputs() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) {
        return [];
      }

      return [
        ...document.querySelectorAll(
          `#ProductSelector-${sectionId} input[data-custom-strap-input]:checked`
        )
      ];
    }

    getMainItemFromFormData(formData) {
      const id = Number(formData.get('id'));
      if (!id) {
        return null;
      }

      const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
      const item = {
        id,
        quantity
      };

      const properties = {};
      for (const [key, value] of formData.entries()) {
        if (!key.startsWith('properties[') || !key.endsWith(']')) {
          continue;
        }
        const propertyName = key.slice(11, -1);
        properties[propertyName] = value;
      }

      if (Object.keys(properties).length) {
        item.properties = properties;
      }

      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) {
        item.selling_plan = sellingPlan;
      }

      return item;
    }

    getSelectedCustomStrapItemsFromInputs(strapInputs, formData, batchId, parentKey) {
      const parentVariantId = Number(formData?.get?.('id') || '');
      const parentProductId = Number(this.dataset.productId || '');
      const quantity = Math.max(1, Number(formData?.get?.('quantity') || '') || 1);

      return (Array.isArray(strapInputs) ? strapInputs : [])
        .map((strapInput, index) => {
          const strapVariantId = Number(
            strapInput?.dataset?.customStrapVariantId || ''
          );
          if (!strapVariantId) {
            return null;
          }

          const strapProductId = Number(
            strapInput?.dataset?.customStrapProductId || ''
          );
          const selectionIndex = index + 1;

          return {
            id: strapVariantId,
            quantity,
            properties: {
              _is_custom_strap: 'true',
              _pair_parent_key: parentKey ? String(parentKey) : '',
              _pair_parent_variant_id: parentVariantId ? String(parentVariantId) : '',
              _pair_parent_product_id: parentProductId ? String(parentProductId) : '',
              _pair_parent_batch_id: batchId ? String(batchId) : '',
              _custom_strap_product_id: strapProductId ? String(strapProductId) : '',
              _custom_strap_selection_index: String(selectionIndex),
              _custom_strap_charge_slot: selectionIndex % 2 === 1 ? 'free' : 'paid'
            }
          };
        })
        .filter(Boolean);
    }

    getCompleteTheLookItem() {
      if (typeof window.BB_getCompleteTheLookSelection !== 'function') {
        return null;
      }
      try {
        return window.BB_getCompleteTheLookSelection();
      } catch (e) {
        return null;
      }
    }

    buildCompleteTheLookPayloadItem(item, batchId) {
      if (!item) return null;
      const parentVariantId = Number(this.form?.querySelector('[name="id"]')?.value || '');
      const parentProductId = Number(this.dataset.productId || '');
      return {
        id: item.id,
        quantity: item.quantity,
        properties: {
          _is_complete_the_look: 'true',
          _pair_parent_variant_id: parentVariantId ? String(parentVariantId) : '',
          _pair_parent_product_id: parentProductId ? String(parentProductId) : '',
          _pair_parent_batch_id: batchId ? String(batchId) : ''
        }
      };
    }

    resetCompleteTheLookSelection() {
      if (typeof window.BB_resetCompleteTheLookSelection === 'function') {
        try {
          window.BB_resetCompleteTheLookSelection();
        } catch (e) {}
      }
    }

    getCompleteTheStrapsItem() {
      if (typeof window.BB_getCompleteTheStrapsSelection !== 'function') {
        return null;
      }
      try {
        return window.BB_getCompleteTheStrapsSelection();
      } catch (e) {
        return null;
      }
    }

    buildCompleteTheStrapsPayloadItem(item, batchId) {
      if (!item) return null;
      const parentVariantId = Number(this.form?.querySelector('[name="id"]')?.value || '');
      const parentProductId = Number(this.dataset.productId || '');
      return {
        id: item.id,
        quantity: item.quantity,
        properties: {
          _is_custom_strap: 'true',
          _is_complete_the_straps: 'true',
          _pair_parent_variant_id: parentVariantId ? String(parentVariantId) : '',
          _pair_parent_product_id: parentProductId ? String(parentProductId) : '',
          _pair_parent_batch_id: batchId ? String(batchId) : ''
        }
      };
    }

    resetCompleteTheStrapsSelection() {
      if (typeof window.BB_resetCompleteTheStrapsSelection === 'function') {
        try {
          window.BB_resetCompleteTheStrapsSelection();
        } catch (e) {}
      }
    }

    getSelectedCustomBottomsInputs() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) return [];
      const selector = document.querySelector(`#ProductSelector-${sectionId}`);
      if (!selector) return [];
      return [...selector.querySelectorAll('input[data-custom-bottoms-input]:checked')];
    }

    getSelectedCustomBottomsItemsFromInputs(bottomsInputs, batchId) {
      const parentVariantId = Number(this.form?.querySelector('[name="id"]')?.value || '');
      const parentProductId = Number(this.dataset.productId || '');
      const quantity = Math.max(1, Number(this.form?.querySelector('[name="quantity"]')?.value || '') || 1);

      const selectedSize = this.form?.querySelector('select[name*="options"], input[name*="options"][type="hidden"]');
      let sizeValue = '';
      if (selectedSize) {
        sizeValue = selectedSize.value;
      } else {
        const checkedSize = this.form?.querySelector('input[name*="options"]:checked');
        if (checkedSize) sizeValue = checkedSize.value;
      }

      return (Array.isArray(bottomsInputs) ? bottomsInputs : [])
        .map((bottomInput, index) => {
          let bottomVariantId = Number(
            bottomInput?.dataset?.customBottomsVariantId || ''
          );

          if (sizeValue && bottomInput.dataset.customBottomsVariants) {
            try {
              const variants = JSON.parse(bottomInput.dataset.customBottomsVariants);
              const match = variants.find(v => {
                const sizeOption = v.option1 || v.option2 || v.option3 || '';
                return sizeOption.toLowerCase() === sizeValue.toLowerCase() && v.available;
              });
              if (match) {
                bottomVariantId = match.id;
              } else {
                const anyMatch = variants.find(v => {
                  const sizeOption = v.option1 || v.option2 || v.option3 || '';
                  return sizeOption.toLowerCase() === sizeValue.toLowerCase();
                });
                if (anyMatch) bottomVariantId = anyMatch.id;
              }
            } catch (e) {}
          }

          if (!bottomVariantId) {
            return null;
          }

          const bottomProductId = Number(
            bottomInput?.dataset?.customBottomsProductId || ''
          );

          return {
            id: bottomVariantId,
            quantity,
            properties: {
              _is_custom_bottom: 'true',
              _pair_parent_variant_id: parentVariantId ? String(parentVariantId) : '',
              _pair_parent_product_id: parentProductId ? String(parentProductId) : '',
              _pair_parent_batch_id: batchId ? String(batchId) : '',
              _custom_bottom_product_id: bottomProductId ? String(bottomProductId) : ''
            }
          };
        })
        .filter(Boolean);
    }


    async onSubmitHandler(event) {
      event.preventDefault();
      const purchaseOptions = document.querySelector(
        'purchase-options'
      );
      if (
        !!purchaseOptions &&
        !purchaseOptions.isOneTimePurchase() &&
        !this.hasAttribute('data-has-selling-plan')
      ) {
        purchaseOptions.showError();
        return;
      }

      this.setAddToCartLoading(true);
      const resetLoading = () => {
        this.setAddToCartLoading(false);
      };
      const timeoutId = window.setTimeout(resetLoading, 20000);

      try {
        const formData = new FormData(this.form);
        if (this.hasAttribute('data-has-pre-order')) {
          formData.append('properties[Pre-order]', '✓');
        }

        const sectionId = this.dataset.sectionId;
        const hasCustomStrapInputs = sectionId
          ? Boolean(
              document.querySelector(
                `#ProductSelector-${sectionId} input[data-custom-strap-input]`
              )
            )
          : false;
        const selectedStrapInputs = hasCustomStrapInputs
          ? this.getSelectedCustomStrapInputs()
          : [];
        
        const hasCustomBottomsInputs = sectionId
          ? Boolean(
              document.querySelector(
                `#ProductSelector-${sectionId} input[data-custom-bottoms-input]`
              )
            )
          : false;
        const selectedBottomsInputs = hasCustomBottomsInputs
          ? this.getSelectedCustomBottomsInputs()
          : [];

        const completeTheLookItem = this.getCompleteTheLookItem();
        const completeTheStrapsItem = this.getCompleteTheStrapsItem();

        // Intercept out of stock straps
        const outOfStockStrap = selectedStrapInputs.find(input => input.disabled || input.classList.contains('is-disabled'));
        if (outOfStockStrap) {
          this.handleErrorMessage('One of the selected straps is out of stock. Please select another strap.');
          window.clearTimeout(timeoutId);
          resetLoading();
          return;
        }

        // Intercept out of stock bottoms
        const outOfStockBottom = selectedBottomsInputs.find(input => input.disabled || input.classList.contains('is-disabled'));
        if (outOfStockBottom) {
          this.handleErrorMessage('One of the selected bottoms is out of stock. Please select another bottom.');
          window.clearTimeout(timeoutId);
          resetLoading();
          return;
        }

        const batchId = (hasCustomStrapInputs || hasCustomBottomsInputs || completeTheLookItem || completeTheStrapsItem)
          ? `${Date.now()}-${Math.random().toString(16).slice(2)}`
          : null;

        if (hasCustomStrapInputs || hasCustomBottomsInputs || completeTheLookItem || completeTheStrapsItem) {
          formData.append('properties[_custom_strap_batch_id]', batchId);
          
          const mainItem = this.getMainItemFromFormData(formData);
          if (!mainItem) {
            window.clearTimeout(timeoutId);
            resetLoading();
            return;
          }

          const strapItems = this.getSelectedCustomStrapItemsFromInputs(
            selectedStrapInputs,
            formData,
            batchId,
            ''
          );

          const bottomsItems = this.getSelectedCustomBottomsItemsFromInputs(
            selectedBottomsInputs,
            batchId
          );

          const completeTheLookPayloadItem = this.buildCompleteTheLookPayloadItem(
            completeTheLookItem,
            batchId
          );

          const completeTheStrapsPayloadItem = this.buildCompleteTheStrapsPayloadItem(
            completeTheStrapsItem,
            batchId
          );

          const payload = {
            items: [
              mainItem,
              ...strapItems,
              ...bottomsItems,
              ...(completeTheLookPayloadItem ? [completeTheLookPayloadItem] : []),
              ...(completeTheStrapsPayloadItem ? [completeTheStrapsPayloadItem] : [])
            ]
          };

          if (this.cartDrawer?.getSectionsToRender) {
            payload.sections = this.cartDrawer
              .getSectionsToRender()
              .map(section => section.section)
              .join(',');
            payload.sections_url = window.location.pathname;
          }

          const addConfig = fetchConfig('json');
          addConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
          addConfig.body = JSON.stringify(payload);

          const addResponse = await fetch(`${routes.cart_add_url}.js`, addConfig);
          const addBody = await addResponse.json();

          if (!addResponse.ok || addBody?.status) {
            this.handleErrorMessage(
              addBody?.description ||
                addBody?.message ||
                addBody?.errors ||
                'Add to cart failed.'
            );
            window.clearTimeout(timeoutId);
            resetLoading();
            return;
          }

          if (this.cartDrawer?.renderContents) {
            if (addBody.sections) {
              this.cartDrawer.renderContents(addBody);
            } else if (typeof this.cartDrawer.getSectionsToRender === 'function') {
              try {
                const sections = this.cartDrawer
                  .getSectionsToRender()
                  ?.map(section => section.section)
                  .filter(Boolean) || [];
                if (sections.length) {
                  const url = `/cart?sections=${encodeURIComponent(sections.join(','))}`;
                  const sectionsResponse = await fetch(url, {
                    headers: { 'X-Requested-With': 'XMLHttpRequest' }
                  });
                  const sectionsBody = await sectionsResponse.json();
                  if (sectionsBody && typeof sectionsBody === 'object') {
                    this.cartDrawer.renderContents({ sections: sectionsBody });
                  } else {
                    this.cartDrawer.open();
                  }
                } else {
                  this.cartDrawer.open();
                }
              } catch (e) {
                this.cartDrawer.open();
              }
            } else {
              this.cartDrawer.open();
            }
          } else if (window.location.pathname === '/cart') {
            window.location.reload();
          }

          this.resetCustomStrapSelections();
          this.resetCustomBottomsSelections();
          this.resetCompleteTheLookSelection();
          this.resetCompleteTheStrapsSelection();
          this.resetProductSelectorToInitialState();
          this.refreshProductSelectorMedia();

          // Emit event to update the page state
          document.dispatchEvent(new CustomEvent('cart:updated'));
        } else {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          if (this.cartDrawer?.getSectionsToRender) {
            formData.append(
              'sections',
              this.cartDrawer
                .getSectionsToRender()
                .map(section => section.section)
            );
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;

          const response = await fetch(`${routes.cart_add_url}`, config);
          const body = await response.json();
          if (!response.ok || body?.status) {
            this.handleErrorMessage(
              body?.description || body?.message || body?.errors || 'Add to cart failed.'
            );
            window.clearTimeout(timeoutId);
            resetLoading();
            return;
          }

          if (this.cartDrawer?.renderContents && body.sections) {
            this.cartDrawer.renderContents(body);
          } else if (window.location.pathname === '/cart') {
            window.location.reload();
          }

          this.resetCustomStrapSelections();
          this.resetCustomBottomsSelections();
          this.resetCompleteTheLookSelection();
          this.resetCompleteTheStrapsSelection();
          this.resetProductSelectorToInitialState();
          this.refreshProductSelectorMedia();
        }

        window.clearTimeout(timeoutId);
        resetLoading();
      } catch (error) {
        console.error(error);
        window.clearTimeout(timeoutId);
        resetLoading();
      }
    }

    handleErrorMessage(errorMessage = false) {
      const errorWrapper = this.querySelector('[data-error-wrapper]');
      if (!errorWrapper) return;

      let message = errorMessage;

      if (typeof errorMessage === 'object' && errorMessage !== null) {
        message = Object.values(errorMessage).flat().join(', ');
      }

      errorWrapper.classList.toggle('hidden', !message);
      errorWrapper.textContent = message || '';
    }

    toggleAddButton(disable, text) {
      const targets = this.getAddToCartButtons();
      const fallback = [
        this.submitButton,
        this.productBar?.querySelector('[name="add"]')
      ].filter(Boolean);
      const buttons = targets.length ? targets : fallback;

      // The sticky bar's button is also one of getAddToCartButtons()
      // (it shares [name="add"][type="submit"] via its form="..."
      // attribute), but it uses .sticky-atc-button-label, not
      // .product-selector__submit-label -- checking only the latter
      // meant this fell through to submitButton.textContent = ...,
      // wiping the whole button (label span, and anything else
      // appended to it, like the Complete The Look price) down to
      // plain text.
      const setButtonText = (submitButton, nextText) => {
        const label = submitButton.querySelector(
          '.product-selector__submit-label, .sticky-atc-button-label'
        );
        if (label) {
          // .product-selector__submit-label can also hold a nested
          // Complete The Look price (appended inside it, not next
          // to it) -- label.textContent = ... would wipe that out
          // too. Replace only the label's own text nodes, leaving
          // any element children (the price) in place.
          Array.from(label.childNodes).forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
              node.remove();
            }
          });
          label.insertBefore(document.createTextNode(nextText), label.firstChild);
        } else {
          submitButton.textContent = nextText;
        }
      };

      buttons.forEach(submitButton => {
        if (disable) {
          submitButton.setAttribute('disabled', 'disabled');
          if (text) {
            setButtonText(submitButton, text);
          }
        } else if (submitButton) {
          submitButton.removeAttribute('disabled');
          const nextText = this.hasAttribute('data-has-selling-plan')
            ? window.theme.strings.addSubscriptionToCart
            : this.hasAttribute('data-has-pre-order')
              ? window.theme.strings.preOrder
              : window.theme.strings.addToCart;
          setButtonText(submitButton, nextText);
        }
      });
    }
  }

  customElements.define('product-form', ProductForm);
})();
